import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { parseCaseStudySections } from '../utils/formatCaseStudy';
import EditCaseStudyModal from '../components/EditCaseStudyModal';
import BadgeAvatar from '../components/BadgeAvatar';
import ForumLoginModal from '../components/ForumLoginModal';

const CaseStudyDetail = ({ user, logout }) => {
  const { id } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [comments, setComments] = useState([]);
  const [quiz, setQuiz] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyToName, setReplyToName] = useState('');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [solution, setSolution] = useState(null);
  const [solutionAvailable, setSolutionAvailable] = useState(false);
  const [collapsedComments, setCollapsedComments] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isForumLoginOpen, setIsForumLoginOpen] = useState(false);

  useEffect(() => {
    fetchCaseStudy();
    fetchComments();
    fetchQuiz();
  }, [id]);

  const fetchCaseStudy = async () => {
    try {
      const response = await api.get(`/case-studies/${id}`);
      setCaseStudy(response.data);
      const endDate = new Date(response.data.discussion_period_ends);
      setSolutionAvailable(new Date() >= endDate);
    } catch (error) {
      console.error('Error fetching case study:', error);
      toast.error('Failed to load case study');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/case-studies/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchQuiz = async () => {
    try {
      const response = await api.get(`/case-studies/${id}/quiz`);
      setQuiz(response.data);
    } catch (error) {
      console.error('Error fetching quiz:', error);
    }
  };

  const handleVote = async () => {
    try {
      await api.post(`/case-studies/${id}/vote`, { vote: 1 });
      toast.success('Vote recorded!');
      fetchCaseStudy();
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote');
    }
  };

  const handleSave = async () => {
    try {
      await api.post(`/case-studies/${id}/save`);
      toast.success('Saved successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await api.post(`/case-studies/${id}/comments`, {
        content: newComment,
        parent_id: replyTo
      });
      setNewComment('');
      setReplyTo(null);
      toast.success('Comment posted!');
      fetchComments();
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    }
  };

  const handleDeleteCaseStudy = async () => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;
    try {
      await api.delete(`/case-studies/${id}`);
      toast.success('Case study deleted successfully');
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error deleting case study:', error);
      toast.error('Failed to delete case study');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await api.delete(`/case-studies/comments/${commentId}`);
      toast.success('Comment deleted successfully');
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const handleQuizSubmit = async () => {
    try {
      const response = await api.post(`/case-studies/${id}/quiz/submit`, {
        answers: quizAnswers
      });
      setQuizResult(response.data);
      setQuizSubmitted(true);
      toast.success(`Score: ${response.data.score}%`);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz');
    }
  };

  const fetchSolution = async () => {
    try {
      const response = await api.get(`/case-studies/${id}/solution`);
      setSolution(response.data.solution);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Solution not yet available');
      } else {
        toast.error('Failed to load solution');
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const countReplies = (comment) => {
    if (!comment.replies || comment.replies.length === 0) return 0;
    let count = comment.replies.length;
    comment.replies.forEach(reply => {
      count += countReplies(reply);
    });
    return count;
  };

  const toggleCollapse = (commentId) => {
    setCollapsedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const organizeComments = () => {
    const commentMap = {};
    const rootComments = [];

    comments.forEach(comment => {
      commentMap[comment.id] = { ...comment, replies: [] };
    });

    comments.forEach(comment => {
      if (comment.parent_id) {
        if (commentMap[comment.parent_id]) {
          commentMap[comment.parent_id].replies.push(commentMap[comment.id]);
        }
      } else {
        rootComments.push(commentMap[comment.id]);
      }
    });

    return rootComments;
  };

  const CommentItem = ({ comment, depth = 0 }) => {
    const isCollapsed = collapsedComments[comment.id];
    const replyCount = countReplies(comment);
    const hasReplies = comment.replies && comment.replies.length > 0;

    // Gaming forum colors based on depth
    const depthColors = [
      'border-l-blue-500 bg-gradient-to-r from-blue-50/50 to-transparent',
      'border-l-purple-500 bg-gradient-to-r from-purple-50/50 to-transparent',
      'border-l-pink-500 bg-gradient-to-r from-pink-50/50 to-transparent',
      'border-l-orange-500 bg-gradient-to-r from-orange-50/50 to-transparent',
    ];

    const colorClass = depth > 0 ? depthColors[Math.min(depth - 1, depthColors.length - 1)] : 'border-l-teal-500';

    return (
      <div className={`${depth > 0 ? 'ml-8 md:ml-12' : ''} mb-3`} data-testid={`comment-${comment.id}`}>
        <div className={`bg-gray-900/5 backdrop-blur-sm rounded-lg border-l-4 ${colorClass} hover:bg-gray-900/10 transition-all duration-200`}>
          <div className="p-4">
            <div className="flex items-start space-x-3">
              <div className="relative flex-shrink-0">
                <BadgeAvatar 
                  commentCount={comment.user_comment_count || 0}
                  size="md"
                  userName={comment.user_name}
                  showLevel={false}
                />
                {depth > 0 && (
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center border-2 border-white">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="font-bold text-gray-900">{comment.user_name}</span>
                  {depth > 0 && (
                    <span className="text-xs bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-2 py-0.5 rounded-full font-semibold">
                      Reply
                    </span>
                  )}
                  <span className="text-xs text-gray-500">•</span>
                  <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                </div>
                <div className="bg-white rounded-lg p-3 mb-3 border border-gray-200 shadow-sm">
                  <p className="text-gray-800 leading-relaxed">{comment.content}</p>
                </div>
                <div className="flex items-center space-x-1 flex-wrap gap-2">
                  <button 
                    className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-full text-xs font-semibold transition-all duration-200 shadow-sm hover:shadow-md" 
                    onClick={() => {
                      setReplyTo(comment.id);
                      setReplyToName(comment.user_name);
                    }}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    <span>Reply</span>
                  </button>
                  {hasReplies && (
                    <button
                      className="flex items-center space-x-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-xs font-semibold transition-all duration-200"
                      onClick={() => toggleCollapse(comment.id)}
                    >
                      <svg className={`w-3.5 h-3.5 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span>{replyCount} {replyCount === 1 ? 'Reply' : 'Replies'}</span>
                    </button>
                  )}
                  <button className="flex items-center space-x-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-xs font-semibold transition-all duration-200">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                    </svg>
                    <span>{comment.upvotes}</span>
                  </button>
                  {user?.is_moderator && (
                    <button 
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-full text-xs font-semibold transition-all duration-200" 
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Delete</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {!isCollapsed && hasReplies && (
          <div className="mt-3 space-y-3">
            {comment.replies.map(reply => (
              <CommentItem key={reply.id} comment={reply} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          <p className="mt-4 text-teal-600 font-medium">Loading case study...</p>
        </div>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="text-center">
          <p className="text-gray-600">Case study not found</p>
          <Link to="/dashboard">
            <Button className="mt-4">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Case Study Club</span>
            </Link>
            <div className="flex items-center space-x-4">
              {user && (
                <>
                  <Link to={`/profile/${user.id}`}>
                    <BadgeAvatar 
                      commentCount={user.comment_count || 0}
                      size="md"
                      userName={user.name}
                      showLevel={false}
                    />
                  </Link>
                  <Button onClick={logout} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6">
                    Logout
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Case Study Header */}
        <div className="bg-white rounded-3xl p-8 shadow-lg mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-4">
                <Badge className={`${caseStudy.difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-base px-4 py-1`}>
                  {caseStudy.difficulty === 'simple' ? '📘 Simple' : '📕 Complex'}
                </Badge>
                {caseStudy.tags.map(tag => (
                  <span key={tag} className="text-sm bg-cyan-50 text-cyan-700 px-3 py-1 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="case-study-title">
                {caseStudy.title}
              </h1>
              <p className="text-lg text-gray-600 mb-4">{caseStudy.description}</p>
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {caseStudy.author_name}
                </span>
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(caseStudy.created_at)}
                </span>
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {caseStudy.view_count} views
                </span>
              </div>
            </div>
            <div className="flex flex-col space-y-3">
              <Button onClick={handleVote} data-testid="vote-btn" className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
                {caseStudy.upvotes}
              </Button>
              <Button onClick={handleSave} data-testid="save-btn" variant="outline" className="rounded-full border-teal-500 text-teal-600 hover:bg-teal-50">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                Save
              </Button>
              {(user?.is_admin || user?.is_moderator || caseStudy?.author_id === user?.id) && (
                <Button onClick={() => setIsEditModalOpen(true)} variant="outline" className="rounded-full border-blue-300 text-blue-600 hover:bg-blue-50">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </Button>
              )}
              {user?.is_moderator && (
                <Button onClick={handleDeleteCaseStudy} variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="content" className="w-full">
          <TabsList className="bg-white rounded-full p-2 shadow-md mb-6">
            <TabsTrigger value="content" data-testid="tab-content" className="rounded-full px-6">Content</TabsTrigger>
            <TabsTrigger value="discussion" data-testid="tab-discussion" className="rounded-full px-6">Discussion ({comments.length})</TabsTrigger>
            {quiz.length > 0 && <TabsTrigger value="quiz" data-testid="tab-quiz" className="rounded-full px-6">Quiz</TabsTrigger>}
            {solutionAvailable && <TabsTrigger value="solution" data-testid="tab-solution" className="rounded-full px-6">Solution</TabsTrigger>}
          </TabsList>

          <TabsContent value="content" data-testid="content-section">
            <div className="space-y-5">
              {(() => {
                const { sections } = parseCaseStudySections(caseStudy.content);
                return sections.map((section, index) => (
                  <div 
                    key={index} 
                    className={`rounded-2xl p-6 shadow-lg border-2 transition-all duration-300 ${
                      section.isTCM 
                        ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-orange-300 hover:border-orange-400' 
                        : 'bg-white border-teal-300 hover:border-teal-400'
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-4">
                      <div className={`w-1 h-8 rounded-full ${section.isTCM ? 'bg-gradient-to-b from-orange-500 to-amber-600' : 'bg-gradient-to-b from-teal-500 to-cyan-600'}`}></div>
                      <h3 className={`text-xl font-bold ${section.isTCM ? 'text-orange-900' : 'text-gray-900'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
                        {section.title}
                      </h3>
                      {section.isTCM && (
                        <span className="text-xs bg-gradient-to-r from-orange-500 to-amber-500 text-white px-3 py-1 rounded-full font-semibold">
                          TCM
                        </span>
                      )}
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                      {section.content.split('\n').map((line, lineIndex) => (
                        line.trim() && (
                          <p key={lineIndex} className="text-gray-800 leading-relaxed mb-2 text-base">
                            {line.startsWith('- ') || line.startsWith('* ') ? (
                              <span className="flex items-start">
                                <span className="text-teal-500 mr-2 mt-1">●</span>
                                <span>{line.substring(2)}</span>
                              </span>
                            ) : (
                              line
                            )}
                          </p>
                        )
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </TabsContent>

          <TabsContent value="discussion" data-testid="discussion-section">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-1 shadow-2xl">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Discussion Forum
                    </h2>
                    <p className="text-sm text-gray-500">{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</p>
                  </div>
                </div>

                {/* Discussion Prompts */}
                <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Discussion Ideas - Share Your Clinical Thinking:
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700">
                    <div className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span><strong>Diagnosis:</strong> What TCM pattern(s) do you identify?</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span><strong>Treatment Principle:</strong> What's your therapeutic strategy?</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span><strong>Channels Involved:</strong> Which meridians are affected?</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span><strong>Zang Fu:</strong> Which organs/systems are imbalanced?</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span><strong>Acupuncture Points:</strong> Your point selection with reasoning & technique</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span><strong>Differential Diagnosis:</strong> What else to consider?</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span><strong>Treatment Approach:</strong> What would you do first?</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span><strong>Clinical Notes:</strong> Any insights, questions, or ideas?</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-3 italic">💡 Share your clinical reasoning and learn from your peers!</p>
                </div>
              
                {/* Comment Form - only for logged in users */}
                {user ? (
                  <form onSubmit={handleCommentSubmit} className="mb-8 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 border-2 border-teal-200">
                    {replyTo && (
                      <div className="mb-4 flex items-center justify-between bg-white px-4 py-3 rounded-xl border-l-4 border-teal-500 shadow-sm">
                        <span className="text-sm text-teal-700 font-semibold flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                          Replying to <span className="font-bold ml-1">{replyToName}</span>
                        </span>
                        <button 
                          type="button" 
                          onClick={() => {
                            setReplyTo(null);
                            setReplyToName('');
                          }} 
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                        >
                          ✕ Cancel
                        </button>
                      </div>
                    )}
                    <Textarea
                      data-testid="comment-input"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={replyTo ? "Write your reply..." : "Join the discussion..."}
                      className="mb-4 min-h-[120px] border-2 border-teal-200 focus:border-teal-400 focus:ring-teal-400 rounded-xl bg-white shadow-sm"
                    />
                    <Button 
                      type="submit" 
                      data-testid="post-comment-btn" 
                      className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white rounded-full px-8 py-2 shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      {replyTo ? '💬 Post Reply' : '💬 Post Comment'}
                    </Button>
                  </form>
                ) : (
                  <div className="mb-8 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border-2 border-teal-200 text-center">
                    <svg className="w-12 h-12 mx-auto mb-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-gray-700 font-medium mb-4">Want to join the discussion?</p>
                    <Button 
                      onClick={() => setIsForumLoginOpen(true)}
                      className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white rounded-full px-8 py-3 shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      Create Forum Account to Comment
                    </Button>
                  </div>
                )}

              {/* Comments List */}
              <div className="space-y-4">
                {organizeComments().length === 0 ? (
                  <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-dashed border-gray-300">
                    <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-gray-600 text-lg font-medium mb-2">No comments yet</p>
                    <p className="text-gray-500 text-sm">Be the first to share your diagnosis and treatment approach!</p>
                    <p className="text-gray-400 text-xs mt-2">💭 What patterns do you see? What points would you use?</p>
                  </div>
                ) : (
                  organizeComments().map(comment => (
                    <CommentItem key={comment.id} comment={comment} />
                  ))
                )}
              </div>
            </div>
          </div>
          </TabsContent>

          {quiz.length > 0 && (
            <TabsContent value="quiz" data-testid="quiz-section">
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Knowledge Check
                </h2>
                {!quizSubmitted ? (
                  <div className="space-y-6">
                    {quiz.map((question, qIndex) => (
                      <div key={question.id} className="border border-gray-200 rounded-xl p-6" data-testid={`quiz-question-${qIndex}`}>
                        <p className="font-semibold text-gray-900 mb-4">{qIndex + 1}. {question.question}</p>
                        <div className="space-y-3">
                          {question.options.map((option, oIndex) => (
                            <label
                              key={oIndex}
                              data-testid={`quiz-option-${qIndex}-${oIndex}`}
                              className={`quiz-option flex items-center p-4 border-2 rounded-lg cursor-pointer ${
                                quizAnswers[question.id] === oIndex ? 'selected' : 'border-gray-200'
                              }`}
                            >
                              <input
                                type="radio"
                                name={question.id}
                                value={oIndex}
                                checked={quizAnswers[question.id] === oIndex}
                                onChange={() => setQuizAnswers({ ...quizAnswers, [question.id]: oIndex })}
                                className="mr-3"
                              />
                              <span className="text-gray-700">{option}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    <Button
                      onClick={handleQuizSubmit}
                      data-testid="submit-quiz-btn"
                      disabled={Object.keys(quizAnswers).length !== quiz.length}
                      className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-8"
                    >
                      Submit Quiz
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="inline-block p-6 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-full mb-4">
                      <svg className="w-16 h-16 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Quiz Complete!</h3>
                    <p className="text-xl text-teal-600 font-semibold" data-testid="quiz-score">
                      Your Score: {quizResult?.score}%
                    </p>
                    <p className="text-gray-600 mt-2">
                      {quizResult?.correct} out of {quizResult?.total} correct
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {solutionAvailable && (
            <TabsContent value="solution" data-testid="solution-section">
              <div className="bg-white rounded-3xl p-8 shadow-lg mb-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Solution
                  </h2>
                </div>
                {!solution ? (
                  <div className="text-center py-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200">
                    <Button onClick={fetchSolution} data-testid="view-solution-btn" className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-full px-8 py-3 text-lg shadow-lg">
                      <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Solution
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {(() => {
                      const { sections } = parseCaseStudySections(solution);
                      return sections.map((section, index) => (
                        <div 
                          key={index} 
                          className={`rounded-2xl p-6 shadow-lg border-2 transition-all duration-300 ${
                            section.isTCM 
                              ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-orange-300 hover:border-orange-400' 
                              : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 hover:border-green-400'
                          }`}
                        >
                          <div className="flex items-center space-x-3 mb-4">
                            <div className={`w-1 h-8 rounded-full ${section.isTCM ? 'bg-gradient-to-b from-orange-500 to-amber-600' : 'bg-gradient-to-b from-green-500 to-emerald-600'}`}></div>
                            <h3 className={`text-xl font-bold ${section.isTCM ? 'text-orange-900' : 'text-green-900'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
                              {section.title}
                            </h3>
                            {section.isTCM && (
                              <span className="text-xs bg-gradient-to-r from-orange-500 to-amber-500 text-white px-3 py-1 rounded-full font-semibold">
                                TCM
                              </span>
                            )}
                          </div>
                          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                            {section.content.split('\n').map((line, lineIndex) => (
                              line.trim() && (
                                <p key={lineIndex} className="text-gray-800 leading-relaxed mb-2 text-base">
                                  {line.startsWith('- ') || line.startsWith('* ') ? (
                                    <span className="flex items-start">
                                      <span className="text-green-500 mr-2 mt-1">●</span>
                                      <span>{line.substring(2)}</span>
                                    </span>
                                  ) : (
                                    line
                                  )}
                                </p>
                              )
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Edit Modal */}
      <EditCaseStudyModal
        caseStudy={caseStudy}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => {
          fetchCaseStudy();
          toast.success('Case study updated successfully!');
        }}
      />
      
      {/* Forum Login Modal */}
      <ForumLoginModal
        isOpen={isForumLoginOpen}
        onClose={() => setIsForumLoginOpen(false)}
        onSuccess={(userData) => {
          window.location.reload(); // Reload to update user state
        }}
      />
    </div>
  );
};

export default CaseStudyDetail;